/** Native touch scrolling with optional, interruptible automatic rotation. */
class BlakeMillHeroSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.bm-hero-slider__track');
    this.slides = [...this.track.children];
    if (this.slides.length < 2) return;
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.dots = [...this.querySelectorAll('[data-slide]')];
    this.slideLinks = this.slides.map((slide) => [...slide.querySelectorAll('a')]);
    this.status = this.querySelector('.bm-hero-slider__status');
    this.drag = null;
    this.dragged = false;
    this.hovered = false;
    this.pauseButton = this.querySelector('[data-pause]');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.dataset.autoplay !== 'true' || this.motion.matches || this.hasAttribute('data-editor');
    this.index = 0;
    this.width = 0;
    this.pendingIndex = null;
    this.slides.forEach((slide, index) => {
      this.setSlideState(index, index === 0);
    });
    this.visible = false;
    this.querySelectorAll('button').forEach((button) => { button.disabled = false; });
    this.dots.forEach((button, index) => button.addEventListener('click', () => this.manual(index), options));
    this.querySelectorAll('[data-step]').forEach((button) => button.addEventListener('click', () => this.manual(this.index + Number(button.dataset.step)), options));
    this.pauseButton?.addEventListener('click', () => this.setPaused(!this.paused), options);
    this.track.addEventListener('scroll', () => {
      if (this.frame) return;
      // Let ResizeObserver process a viewport change before interpreting the
      // scroll offset with the cached width (including RTL scroll anchoring).
      this.frame = requestAnimationFrame(() => {
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      });
    }, { ...options, passive: true });
    this.track.addEventListener('keydown', (event) => {
      if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.manual(this.index + (event.key === 'ArrowRight' ? 1 : -1) * (this.rtl ? -1 : 1));
    }, options);
    this.track.addEventListener('pointerdown', (event) => {
      const left = this.track.scrollLeft;
      this.setPaused(true);
      this.dragged = false;
      if (event.pointerType === 'mouse' && event.button === 0) this.drag = { id: event.pointerId, x: event.clientX, left };
    }, options);
    this.track.addEventListener('pointermove', (event) => {
      if (!this.drag || this.drag.id !== event.pointerId) return;
      const delta = event.clientX - this.drag.x;
      if (!this.dragged && Math.abs(delta) < 6) return;
      this.dragged = true;
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture(event.pointerId);
      this.track.scrollLeft = this.drag.left - delta;
      event.preventDefault();
    }, options);
    const endDrag = () => {
      if (!this.drag) return;
      const id = this.drag.id;
      this.drag = null;
      const index = this.nearest();
      if (this.track.hasPointerCapture(id)) this.track.releasePointerCapture(id);
      this.track.classList.remove('is-dragging');
      if (this.dragged) this.goTo(index);
    };
    window.addEventListener('pointerup', endDrag, options);
    this.track.addEventListener('pointercancel', endDrag, options);
    this.track.addEventListener('lostpointercapture', endDrag, options);
    this.track.addEventListener('dragstart', (event) => event.preventDefault(), options);
    this.track.addEventListener('click', (event) => {
      if (!this.dragged || event.detail === 0) return;
      event.preventDefault();
      event.stopPropagation();
      this.dragged = false;
    }, { ...options, capture: true });
    this.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, options);
    this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, options);
    this.addEventListener('focusin', (event) => {
      if (event.target !== this.pauseButton) this.setPaused(true);
    }, options);
    document.addEventListener('visibilitychange', () => this.schedule(), options);
    this.motion.addEventListener('change', () => {
      if (this.motion.matches) this.setPaused(true);
    }, options);
    this.addEventListener('shopify:block:select', (event) => {
      const index = this.slides.indexOf(event.target.closest('.bm-hero__slide'));
      if (index >= 0) this.manual(index, true);
    }, options);
    this.resize = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      if (!width || width === this.width) return;
      this.width = width;
      this.rtl = getComputedStyle(this.track).direction === 'rtl';
      cancelAnimationFrame(this.frame);
      this.frame = null;
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.goTo(this.pendingIndex ?? this.index, true);
        this.schedule();
      });
    });
    this.resize.observe(this.track);
    this.intersection = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.schedule();
    });
    this.intersection.observe(this);
    this.setPaused(this.paused);
  }

  nearest() { return Math.max(0, Math.min(this.slides.length - 1, Math.round(Math.abs(this.track.scrollLeft) / (this.width || 1)))); }

  manual(index, instant = false) {
    this.setPaused(true);
    this.goTo(index, instant);
  }

  goTo(index, instant = false) {
    const next = (index + this.slides.length) % this.slides.length;
    if (!this.width || this.resizeFrame) {
      this.pendingIndex = next;
      return;
    }
    this.pendingIndex = null;
    this.track.scrollTo({ left: next * this.width * (this.rtl ? -1 : 1), behavior: instant || this.motion.matches ? 'instant' : 'smooth' });
  }

  update() {
    if (!this.width || this.resizeFrame) return;
    const index = this.nearest();
    if (index === this.index) return;
    const previous = this.index;
    this.index = index;
    this.setSlideState(previous, false);
    this.setSlideState(index, true);
    const message = `Slide ${index + 1} of ${this.slides.length}`;
    if (this.paused && this.status.textContent !== message) this.status.textContent = message;
  }

  setSlideState(index, active) {
    this.slideLinks[index].forEach((link) => {
      const tabIndex = active ? 0 : -1;
      if (link.tabIndex !== tabIndex) link.tabIndex = tabIndex;
    });
    const hidden = String(!active);
    if (this.slides[index].getAttribute('aria-hidden') !== hidden) this.slides[index].setAttribute('aria-hidden', hidden);
    const dot = this.dots[index];
    if (active && dot.getAttribute('aria-current') !== 'true') dot.setAttribute('aria-current', 'true');
    else if (!active && dot.hasAttribute('aria-current')) dot.removeAttribute('aria-current');
  }

  setPaused(paused) {
    this.paused = paused;
    if (this.pauseButton) {
      const label = paused ? 'Play slideshow' : 'Pause slideshow';
      const pressed = String(paused);
      const icon = paused ? '▶' : 'Ⅱ';
      if (this.pauseButton.getAttribute('aria-label') !== label) this.pauseButton.setAttribute('aria-label', label);
      if (this.pauseButton.getAttribute('aria-pressed') !== pressed) this.pauseButton.setAttribute('aria-pressed', pressed);
      if (this.pauseButton.firstElementChild.textContent !== icon) this.pauseButton.firstElementChild.textContent = icon;
    }
    this.schedule();
  }

  schedule() {
    clearTimeout(this.timer);
    if (!this.width || this.paused || this.hovered || !this.visible || document.hidden) return;
    this.timer = setTimeout(() => {
      this.goTo(this.index + 1);
      this.schedule();
    }, Math.max(3000, Number(this.dataset.interval) || 6000));
  }

  disconnectedCallback() {
    this.events?.abort();
    this.resize?.disconnect();
    this.intersection?.disconnect();
    clearTimeout(this.timer);
    cancelAnimationFrame(this.frame);
    this.frame = null;
    cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = null;
    this.pendingIndex = null;
    this.drag = null;
    this.dragged = false;
    this.hovered = false;
    this.track?.classList.remove('is-dragging');
  }
}
if (!customElements.get('bm-hero-slider')) customElements.define('bm-hero-slider', BlakeMillHeroSlider);
